import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, Play } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 21: NOIR
 * Style: Dark Cinematic Luxury
 * Best for: Premium Indian fashion, Bridal couture, High-end ethnic wear
 * Near-black theme with champagne gold accents
 */
const Template21 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const [email, setEmail] = useState('');
    const touchStartX = useRef(0);
    const videoRef = useRef(null);

    // Collection Stories
    const collections = [
        {
            name: 'The Midnight Edit',
            description: 'A curation of timeless silhouettes draped in darkness and woven with gold.',
            image: 'https://images.pexels.com/photos/30706568/pexels-photo-30706568.jpeg?auto=compress&cs=tinysrgb&w=1920'
        },
        {
            name: 'Bridal Noir',
            description: 'Where bridal tradition meets the allure of the night. Opulent, bold, unforgettable.',
            image: 'https://images.pexels.com/photos/9927983/pexels-photo-9927983.jpeg?auto=compress&cs=tinysrgb&w=1920'
        },
        {
            name: 'Heritage Reimagined',
            description: 'Ancient craftsmanship reborn through a contemporary lens of shadow and light.',
            image: 'https://images.pexels.com/photos/17570989/pexels-photo-17570989.jpeg?auto=compress&cs=tinysrgb&w=1920'
        }
    ];

    // Products
    const products = [
        { id: 1, name: 'Midnight Silk Lehenga', price: '\u20B924,999', image: 'https://images.pexels.com/photos/9419032/pexels-photo-9419032.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 2, name: 'Noir Banarasi Saree', price: '\u20B918,500', image: 'https://images.pexels.com/photos/11707056/pexels-photo-11707056.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 3, name: 'Black Zardozi Sherwani', price: '\u20B932,000', image: 'https://images.pexels.com/photos/6687182/pexels-photo-6687182.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 4, name: 'Emerald Velvet Anarkali', price: '\u20B915,999', image: 'https://images.pexels.com/photos/7123307/pexels-photo-7123307.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 5, name: 'Onyx Silk Dupatta Set', price: '\u20B912,499', image: 'https://images.pexels.com/photos/5595710/pexels-photo-5595710.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 6, name: 'Champagne Gold Saree', price: '\u20B921,000', image: 'https://images.pexels.com/photos/2723623/pexels-photo-2723623.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 7, name: 'Royal Crimson Lehenga', price: '\u20B928,999', image: 'https://images.pexels.com/photos/9901808/pexels-photo-9901808.jpeg?auto=compress&cs=tinysrgb&w=800' },
        { id: 8, name: 'Ivory Chanderi Kurta', price: '\u20B98,999', image: 'https://images.pexels.com/photos/17611651/pexels-photo-17611651.jpeg?auto=compress&cs=tinysrgb&w=800' }
    ];

    // Categories
    const categories = [
        { name: 'Bridal', image: 'https://images.pexels.com/photos/12995512/pexels-photo-12995512.jpeg?auto=compress&cs=tinysrgb&w=1260' },
        { name: 'Menswear', image: 'https://images.pexels.com/photos/34423732/pexels-photo-34423732.jpeg?auto=compress&cs=tinysrgb&w=1260' },
        { name: 'Sarees', image: 'https://images.pexels.com/photos/28867973/pexels-photo-28867973.jpeg?auto=compress&cs=tinysrgb&w=1260' },
        { name: 'Indo-Western', image: 'https://images.pexels.com/photos/32151740/pexels-photo-32151740.jpeg?auto=compress&cs=tinysrgb&w=1260' },
        { name: 'Accessories', image: 'https://images.pexels.com/photos/3221176/pexels-photo-3221176.jpeg?auto=compress&cs=tinysrgb&w=1260' }
    ];

    // Testimonials
    const testimonials = [
        {
            text: 'The craftsmanship is extraordinary. Every stitch tells a story of heritage and luxury. My bridal lehenga was nothing short of a masterpiece.',
            name: 'Priya Mehta',
            city: 'Mumbai'
        },
        {
            text: 'NOIR understands the art of making you feel like royalty. The sherwani I ordered for my wedding exceeded every expectation.',
            name: 'Arjun Kapoor',
            city: 'Delhi'
        },
        {
            text: 'I have never worn anything so exquisite. The silk saree drapes like a dream and the zardozi work is unparalleled. Truly cinematic fashion.',
            name: 'Kavitha Reddy',
            city: 'Hyderabad'
        }
    ];

    // Auto-rotate testimonials
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Touch swipe for testimonials
    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    return (
        <div style={{ fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif" }} className="w-full min-h-screen" >
            {/* Google Fonts */}
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400;1,500&family=DM+Sans:wght@300;400;500;600&display=swap');
            `}</style>

            {/* ===== SECTION 1: VIDEO HERO ===== */}
            <section className="relative min-h-screen w-full overflow-hidden flex items-center justify-center" style={{ backgroundColor: '#0A0A0A' }}>
                {/* Video Background */}
                <video
                    ref={videoRef}
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster="https://images.pexels.com/photos/30706568/pexels-photo-30706568.jpeg?auto=compress&cs=tinysrgb&w=1920"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ opacity: 0.6 }}
                >
                    <source src="https://assets.mixkit.co/videos/805/805-720.mp4" type="video/mp4" />
                </video>

                {/* Dark Gradient Overlay */}
                <div
                    className="absolute inset-0"
                    style={{
                        background: 'linear-gradient(to bottom, transparent 0%, rgba(10,10,10,0.4) 50%, #0A0A0A 100%)'
                    }}
                />

                {/* Hero Content */}
                <div className="relative z-10 text-center px-4 sm:px-6">
                    <p
                        className="text-xs sm:text-sm tracking-[0.4em] uppercase mb-6"
                        style={{ color: '#C9A96E', fontFamily: "'DM Sans', sans-serif" }}
                    >
                        Luxury Indian Fashion
                    </p>
                    <h1
                        className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-light mb-6 tracking-wider"
                        style={{
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                            color: '#F5F0EB',
                            letterSpacing: '0.15em'
                        }}
                    >
                        NOIR
                    </h1>
                    <p
                        className="text-base sm:text-lg lg:text-xl font-light mb-10 max-w-lg mx-auto"
                        style={{
                            color: '#8A8A7A',
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                            fontStyle: 'italic'
                        }}
                    >
                        Where Darkness Meets Elegance
                    </p>
                    <button
                        onClick={() => navigate('/template21/shop')}
                        className="inline-flex items-center gap-3 px-8 py-3.5 sm:px-10 sm:py-4 text-sm tracking-[0.2em] uppercase transition-all duration-300"
                        style={{
                            backgroundColor: 'transparent',
                            border: '1px solid #C9A96E',
                            color: '#C9A96E',
                            fontFamily: "'DM Sans', sans-serif"
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#C9A96E';
                            e.currentTarget.style.color = '#0A0A0A';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#C9A96E';
                        }}
                    >
                        Explore Collection <ArrowRight className="w-4 h-4" />
                    </button>
                </div>

                {/* Scroll Indicator */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
                    <div className="w-px h-12 opacity-40" style={{ backgroundColor: '#C9A96E' }}>
                        <div
                            className="w-full h-1/2"
                            style={{
                                backgroundColor: '#C9A96E',
                                animation: 'scrollPulse 2s ease-in-out infinite'
                            }}
                        />
                    </div>
                </div>
                <style>{`
                    @keyframes scrollPulse {
                        0%, 100% { opacity: 0.3; transform: translateY(0); }
                        50% { opacity: 1; transform: translateY(12px); }
                    }
                `}</style>
            </section>

            {/* ===== SECTION 2: COLLECTION STORIES ===== */}
            {/* Mobile: stacked full-width */}
            <section className="lg:hidden" style={{ backgroundColor: '#0A0A0A' }}>
                <div className="px-4 py-10">
                    <p
                        className="text-xs tracking-[0.3em] uppercase mb-2 text-center"
                        style={{ color: '#8A8A7A' }}
                    >
                        Collections
                    </p>
                    <h2
                        className="text-2xl font-light mb-8 text-center"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                    >
                        Our Stories
                    </h2>
                    <div className="flex flex-col gap-4">
                        {collections.map((collection, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template21/shop')}
                                className="relative aspect-[3/4] overflow-hidden cursor-pointer group"
                            >
                                <img
                                    src={collection.image}
                                    alt={collection.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: 'linear-gradient(to top, rgba(10,10,10,0.9) 0%, rgba(10,10,10,0.3) 40%, transparent 100%)'
                                    }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-6">
                                    <h3
                                        className="text-2xl font-light mb-2"
                                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                                    >
                                        {collection.name}
                                    </h3>
                                    <p className="text-sm mb-3 leading-relaxed" style={{ color: '#8A8A7A' }}>
                                        {collection.description}
                                    </p>
                                    <span
                                        className="text-xs tracking-[0.15em] uppercase inline-flex items-center gap-2"
                                        style={{ color: '#C9A96E' }}
                                    >
                                        Discover <ArrowRight className="w-3 h-3" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop: 3-column grid */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#0A0A0A' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p
                        className="text-sm tracking-[0.3em] uppercase mb-3 text-center"
                        style={{ color: '#8A8A7A' }}
                    >
                        Collections
                    </p>
                    <h2
                        className="text-4xl font-light mb-16 text-center"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                    >
                        Our Stories
                    </h2>
                    <div className="grid grid-cols-3 gap-6">
                        {collections.map((collection, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template21/shop')}
                                className="relative aspect-[3/4] overflow-hidden cursor-pointer group"
                            >
                                <img
                                    src={collection.image}
                                    alt={collection.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: 'linear-gradient(to top, rgba(10,10,10,0.9) 0%, rgba(10,10,10,0.3) 40%, transparent 100%)'
                                    }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-8">
                                    <h3
                                        className="text-3xl font-light mb-3"
                                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                                    >
                                        {collection.name}
                                    </h3>
                                    <p className="text-sm mb-4 leading-relaxed" style={{ color: '#8A8A7A' }}>
                                        {collection.description}
                                    </p>
                                    <span
                                        className="text-sm tracking-[0.15em] uppercase inline-flex items-center gap-2 group-hover:gap-3 transition-all duration-300"
                                        style={{ color: '#C9A96E' }}
                                    >
                                        Discover <ArrowRight className="w-4 h-4" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 3: SIGNATURE SPOTLIGHT ===== */}
            {/* Mobile: stacked */}
            <section className="lg:hidden" style={{ backgroundColor: '#0A0A0A' }}>
                <div className="w-full aspect-[4/5] overflow-hidden">
                    <img
                        src="https://images.pexels.com/photos/9927983/pexels-photo-9927983.jpeg?auto=compress&cs=tinysrgb&w=1920"
                        alt="The Midnight Collection"
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="px-5 py-10">
                    <p
                        className="text-xs tracking-[0.3em] uppercase mb-3"
                        style={{ color: '#8A8A7A' }}
                    >
                        Signature Collection
                    </p>
                    <h2
                        className="text-3xl font-light mb-5 leading-tight"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                    >
                        The Midnight Collection
                    </h2>
                    <p className="text-sm leading-relaxed mb-6" style={{ color: '#F5F0EB' }}>
                        Born from the depths of midnight, this collection weaves the ancient art of Indian
                        textile craftsmanship with an unapologetically modern sensibility. Each piece is a
                        meditation on shadow and light, created by master artisans who have inherited
                        centuries of tradition. From hand-embroidered zardozi to the finest Banarasi silks,
                        every garment tells the story of a heritage that refuses to be forgotten.
                    </p>
                    <button
                        onClick={() => navigate('/template21/shop')}
                        className="inline-flex items-center gap-3 px-8 py-3.5 text-sm tracking-[0.2em] uppercase transition-all duration-300"
                        style={{
                            backgroundColor: 'transparent',
                            border: '1px solid #C9A96E',
                            color: '#C9A96E'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#C9A96E';
                            e.currentTarget.style.color = '#0A0A0A';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#C9A96E';
                        }}
                    >
                        Shop Now <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Desktop: image left 60%, text right 40% */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#0A0A0A' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-5 gap-12 items-center">
                        <div className="col-span-3 aspect-[4/5] overflow-hidden">
                            <img
                                src="https://images.pexels.com/photos/9927983/pexels-photo-9927983.jpeg?auto=compress&cs=tinysrgb&w=1920"
                                alt="The Midnight Collection"
                                className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700"
                            />
                        </div>
                        <div className="col-span-2">
                            <p
                                className="text-sm tracking-[0.3em] uppercase mb-4"
                                style={{ color: '#8A8A7A' }}
                            >
                                Signature Collection
                            </p>
                            <h2
                                className="text-4xl xl:text-5xl font-light mb-8 leading-tight"
                                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                            >
                                The Midnight Collection
                            </h2>
                            <p className="leading-relaxed mb-8" style={{ color: '#F5F0EB' }}>
                                Born from the depths of midnight, this collection weaves the ancient art of Indian
                                textile craftsmanship with an unapologetically modern sensibility. Each piece is a
                                meditation on shadow and light, created by master artisans who have inherited
                                centuries of tradition.
                            </p>
                            <p className="text-sm leading-relaxed mb-10" style={{ color: '#8A8A7A' }}>
                                From hand-embroidered zardozi to the finest Banarasi silks,
                                every garment tells the story of a heritage that refuses to be forgotten.
                            </p>
                            <button
                                onClick={() => navigate('/template21/shop')}
                                className="inline-flex items-center gap-3 px-10 py-4 text-sm tracking-[0.2em] uppercase transition-all duration-300"
                                style={{
                                    backgroundColor: 'transparent',
                                    border: '1px solid #C9A96E',
                                    color: '#C9A96E'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = '#C9A96E';
                                    e.currentTarget.style.color = '#0A0A0A';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = '#C9A96E';
                                }}
                            >
                                Shop Now <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 4: PRODUCT GRID ===== */}
            {/* Mobile: 2-col grid */}
            <section className="lg:hidden py-10" style={{ backgroundColor: '#0A0A0A' }}>
                <div className="px-4">
                    <h2
                        className="text-2xl font-light mb-8 text-center"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                    >
                        Curated Selections
                    </h2>
                    <div className="grid grid-cols-2 gap-3">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template21/shop')}
                                className="cursor-pointer group"
                                style={{
                                    backgroundColor: '#141414',
                                    border: '1px solid #2A2A2A',
                                    transition: 'border-color 0.3s ease'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#C9A96E'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2A2A2A'; }}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                                        style={{ backgroundColor: 'rgba(20,20,20,0.8)' }}
                                    >
                                        <Heart
                                            className="w-4 h-4"
                                            style={{
                                                color: wishlist[product.id] ? '#C9A96E' : '#8A8A7A',
                                                fill: wishlist[product.id] ? '#C9A96E' : 'none'
                                            }}
                                        />
                                    </button>
                                </div>
                                <div className="p-3">
                                    <h3 className="text-xs font-normal line-clamp-1 mb-1" style={{ color: '#F5F0EB' }}>
                                        {product.name}
                                    </h3>
                                    <p className="text-xs font-medium" style={{ color: '#C9A96E' }}>{product.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={() => navigate('/template21/shop')}
                        className="w-full mt-6 py-3.5 text-sm tracking-[0.15em] uppercase transition-all duration-300"
                        style={{
                            backgroundColor: 'transparent',
                            border: '1px solid #C9A96E',
                            color: '#C9A96E'
                        }}
                    >
                        View All Collections
                    </button>
                </div>
            </section>

            {/* Desktop: 4-col grid */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#0A0A0A' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2
                        className="text-4xl font-light mb-16 text-center"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                    >
                        Curated Selections
                    </h2>
                    <div className="grid grid-cols-4 gap-6">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template21/shop')}
                                className="cursor-pointer group"
                                style={{
                                    backgroundColor: '#141414',
                                    border: '1px solid #2A2A2A',
                                    transition: 'border-color 0.3s ease'
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#C9A96E'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#2A2A2A'; }}
                            >
                                <div className="relative aspect-[3/4] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                    />
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-3 right-3 w-9 h-9 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
                                        style={{ backgroundColor: 'rgba(20,20,20,0.85)' }}
                                    >
                                        <Heart
                                            className="w-4 h-4"
                                            style={{
                                                color: wishlist[product.id] ? '#C9A96E' : '#8A8A7A',
                                                fill: wishlist[product.id] ? '#C9A96E' : 'none'
                                            }}
                                        />
                                    </button>
                                </div>
                                <div className="p-4">
                                    <h3 className="text-sm font-normal mb-1.5 group-hover:underline underline-offset-4 transition-all" style={{ color: '#F5F0EB' }}>
                                        {product.name}
                                    </h3>
                                    <p className="text-sm font-medium" style={{ color: '#C9A96E' }}>{product.price}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 5: ATELIER STORY ===== */}
            <section className="relative py-24 lg:py-32 overflow-hidden">
                {/* Background Image */}
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage: "url('https://images.pexels.com/photos/3965543/pexels-photo-3965543.jpeg?auto=compress&cs=tinysrgb&w=1260')"
                    }}
                />
                {/* Heavy Dark Overlay */}
                <div className="absolute inset-0" style={{ backgroundColor: 'rgba(10,10,10,0.85)' }} />

                <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="w-12 h-px mx-auto mb-8" style={{ backgroundColor: '#C9A96E' }} />
                    <blockquote
                        className="text-2xl sm:text-3xl lg:text-4xl font-light italic mb-8 leading-relaxed"
                        style={{
                            fontFamily: "'Cormorant Garamond', Georgia, serif",
                            color: '#C9A96E'
                        }}
                    >
                        "Where tradition meets the art of darkness"
                    </blockquote>
                    <p
                        className="text-sm sm:text-base leading-relaxed max-w-xl mx-auto mb-6"
                        style={{ color: '#8A8A7A' }}
                    >
                        Our atelier is where centuries-old Indian craftsmanship converges with a bold,
                        contemporary vision. Every garment is hand-finished by artisans whose families have
                        woven stories into fabric for generations. We believe luxury lies not in excess, but
                        in the quiet mastery of detail.
                    </p>
                    <div className="w-12 h-px mx-auto" style={{ backgroundColor: '#C9A96E' }} />
                </div>
            </section>

            {/* ===== SECTION 5.5: CATEGORIES ===== */}
            {/* Mobile: horizontal scroll */}
            <section className="lg:hidden py-10" style={{ backgroundColor: '#0A0A0A' }}>
                <div className="px-4">
                    <h2
                        className="text-2xl font-light mb-6 text-center"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                    >
                        Shop by Category
                    </h2>
                    <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory scrollbar-hide pb-2">
                        {categories.map((cat, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template21/shop')}
                                className="flex-shrink-0 w-[60vw] snap-start cursor-pointer group relative overflow-hidden"
                                style={{ aspectRatio: '3/4' }}
                            >
                                <img
                                    src={cat.image}
                                    alt={cat.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: 'linear-gradient(to top, rgba(10,10,10,0.8) 0%, rgba(10,10,10,0.2) 50%, transparent 100%)'
                                    }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-4">
                                    <h3
                                        className="text-lg font-light"
                                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                                    >
                                        {cat.name}
                                    </h3>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop: 5-col categories */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#0A0A0A' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2
                        className="text-4xl font-light mb-16 text-center"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                    >
                        Shop by Category
                    </h2>
                    <div className="grid grid-cols-5 gap-4">
                        {categories.map((cat, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template21/shop')}
                                className="relative aspect-[3/4] overflow-hidden cursor-pointer group"
                            >
                                <img
                                    src={cat.image}
                                    alt={cat.name}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                                />
                                <div
                                    className="absolute inset-0"
                                    style={{
                                        background: 'linear-gradient(to top, rgba(10,10,10,0.8) 0%, rgba(10,10,10,0.2) 50%, transparent 100%)'
                                    }}
                                />
                                <div className="absolute bottom-0 left-0 right-0 p-5">
                                    <h3
                                        className="text-xl font-light"
                                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                                    >
                                        {cat.name}
                                    </h3>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SECTION 6: TESTIMONIALS ===== */}
            {/* Mobile: swipeable carousel */}
            <section className="lg:hidden py-10" style={{ backgroundColor: '#0A0A0A' }}>
                <div className="px-4">
                    <p
                        className="text-xs tracking-[0.3em] uppercase mb-2 text-center"
                        style={{ color: '#8A8A7A' }}
                    >
                        Testimonials
                    </p>
                    <h2
                        className="text-2xl font-light mb-8 text-center"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                    >
                        Voices of NOIR
                    </h2>
                    <div
                        className="overflow-hidden"
                        onTouchStart={handleTouchStart}
                        onTouchEnd={handleTouchEnd}
                    >
                        <div
                            className="flex transition-transform duration-500 ease-out"
                            style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
                        >
                            {testimonials.map((testimonial, index) => (
                                <div key={index} className="w-full flex-shrink-0 px-1">
                                    <div
                                        className="p-6 text-center"
                                        style={{ backgroundColor: '#141414', border: '1px solid #2A2A2A' }}
                                    >
                                        <div className="flex justify-center gap-1 mb-4">
                                            {[...Array(5)].map((_, i) => (
                                                <Star
                                                    key={i}
                                                    className="w-4 h-4"
                                                    style={{ color: '#C9A96E', fill: '#C9A96E' }}
                                                />
                                            ))}
                                        </div>
                                        <p
                                            className="text-sm leading-relaxed mb-5 italic"
                                            style={{
                                                color: '#F5F0EB',
                                                fontFamily: "'Cormorant Garamond', Georgia, serif",
                                                fontSize: '1rem'
                                            }}
                                        >
                                            "{testimonial.text}"
                                        </p>
                                        <p className="text-sm font-medium" style={{ color: '#C9A96E' }}>
                                            {testimonial.name}
                                        </p>
                                        <p className="text-xs mt-0.5" style={{ color: '#8A8A7A' }}>
                                            {testimonial.city}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-5">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    className="h-2 rounded-full transition-all duration-300"
                                    style={{
                                        width: activeTestimonial === i ? '24px' : '8px',
                                        backgroundColor: activeTestimonial === i ? '#C9A96E' : '#2A2A2A'
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop: testimonials */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#0A0A0A' }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <p
                        className="text-sm tracking-[0.3em] uppercase mb-3 text-center"
                        style={{ color: '#8A8A7A' }}
                    >
                        Testimonials
                    </p>
                    <h2
                        className="text-4xl font-light mb-16 text-center"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                    >
                        Voices of NOIR
                    </h2>
                    <div className="overflow-hidden" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                        <div
                            className="flex transition-transform duration-500 ease-out"
                            style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
                        >
                            {testimonials.map((testimonial, index) => (
                                <div key={index} className="w-full flex-shrink-0 px-4">
                                    <div
                                        className="max-w-2xl mx-auto p-12 text-center"
                                        style={{ backgroundColor: '#141414', border: '1px solid #2A2A2A' }}
                                    >
                                        <div className="flex justify-center gap-1.5 mb-6">
                                            {[...Array(5)].map((_, i) => (
                                                <Star
                                                    key={i}
                                                    className="w-5 h-5"
                                                    style={{ color: '#C9A96E', fill: '#C9A96E' }}
                                                />
                                            ))}
                                        </div>
                                        <p
                                            className="text-xl lg:text-2xl leading-relaxed mb-8 italic font-light"
                                            style={{
                                                color: '#F5F0EB',
                                                fontFamily: "'Cormorant Garamond', Georgia, serif"
                                            }}
                                        >
                                            "{testimonial.text}"
                                        </p>
                                        <p className="text-sm font-medium tracking-wider" style={{ color: '#C9A96E' }}>
                                            {testimonial.name}
                                        </p>
                                        <p className="text-sm mt-1" style={{ color: '#8A8A7A' }}>
                                            {testimonial.city}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-8">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    className="h-2 rounded-full transition-all duration-300"
                                    style={{
                                        width: activeTestimonial === i ? '32px' : '8px',
                                        backgroundColor: activeTestimonial === i ? '#C9A96E' : '#2A2A2A'
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== SECTION 7: NEWSLETTER ===== */}
            {/* Mobile */}
            <section className="lg:hidden py-10" style={{ backgroundColor: '#141414' }}>
                <div className="px-5 text-center">
                    <div className="w-10 h-px mx-auto mb-6" style={{ backgroundColor: '#C9A96E' }} />
                    <h2
                        className="text-2xl font-light mb-3"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                    >
                        Enter the World of NOIR
                    </h2>
                    <p className="text-sm mb-6" style={{ color: '#8A8A7A' }}>
                        Exclusive previews, behind-the-scenes access, and first look at new collections.
                    </p>
                    <div className="flex flex-col gap-3 max-w-sm mx-auto">
                        <input
                            type="email"
                            placeholder="Your email address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="px-5 py-3.5 text-sm focus:outline-none transition-colors"
                            style={{
                                backgroundColor: '#141414',
                                border: '1px solid #C9A96E',
                                color: '#F5F0EB',
                                fontFamily: "'DM Sans', sans-serif"
                            }}
                        />
                        <button
                            className="w-full px-8 py-3.5 text-sm tracking-[0.2em] uppercase transition-all duration-300"
                            style={{
                                backgroundColor: '#C9A96E',
                                color: '#0A0A0A',
                                fontFamily: "'DM Sans', sans-serif"
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D4B87A'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C9A96E'; }}
                        >
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* Desktop */}
            <section className="hidden lg:block py-24" style={{ backgroundColor: '#141414' }}>
                <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="w-12 h-px mx-auto mb-8" style={{ backgroundColor: '#C9A96E' }} />
                    <h2
                        className="text-4xl font-light mb-4"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#C9A96E' }}
                    >
                        Enter the World of NOIR
                    </h2>
                    <p className="mb-10" style={{ color: '#8A8A7A' }}>
                        Exclusive previews, behind-the-scenes access, and first look at new collections.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Your email address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="flex-1 px-6 py-4 text-sm focus:outline-none transition-colors"
                            style={{
                                backgroundColor: '#141414',
                                border: '1px solid #C9A96E',
                                color: '#F5F0EB',
                                fontFamily: "'DM Sans', sans-serif"
                            }}
                        />
                        <button
                            className="px-8 py-4 text-sm tracking-[0.2em] uppercase transition-all duration-300"
                            style={{
                                backgroundColor: '#C9A96E',
                                color: '#0A0A0A',
                                fontFamily: "'DM Sans', sans-serif"
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#D4B87A'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#C9A96E'; }}
                        >
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== FOOTER ACCENT LINE ===== */}
            <div className="w-full h-px" style={{ backgroundColor: '#2A2A2A' }} />
        </div>
    );
};

export default Template21;
